import {useEffect, useState, useRef} from 'react';
import {QEPInput} from '@qep/shared/src/components/QEPInput/qep-input';
import {useDispatch, useSelector} from 'react-redux';
import {getQepSystem, usePreferences} from '@qep/shared/src';
import {LoadingIndicator} from '../loading-indicator/loading-indicator.js';
import {setDefaultAlgoDuration} from './store/actions.js';
import {type QepPreferencesState} from './store/reducer.js';
import './QEPPrefs.scss';

type RootReducer = {
  qepPreferencesReducer: QepPreferencesState;
};

const defaultAlgoDurationMinutes = 15;
const minAlgoDurationMinutes = 1;
const maxAlgoDurationMinutes = 7 * 60;
const invalidAlgoDurationMessage = 'Algo duration is invalid';

/** Parses a candidate default algo duration when it is a whole minute count inside the supported seven-hour range. */
function parseValidDefaultAlgoDuration(value: string): number | undefined {
  const trimmedValue = value.trim();
  if (!/^\d+$/.test(trimmedValue)) {
    return undefined;
  }

  const duration = Number(trimmedValue);
  return Number.isInteger(duration) && duration >= minAlgoDurationMinutes && duration <= maxAlgoDurationMinutes
    ? duration
    : undefined;
}

/** Converts primitive preference payloads to strings while treating unsupported stored shapes as invalid input. */
function stringifyDefaultAlgoDuration(rawValue: unknown): string {
  if (rawValue === null || rawValue === undefined) {
    return String(defaultAlgoDurationMinutes);
  }

  return typeof rawValue === 'number' || typeof rawValue === 'string'
    ? String(rawValue)
    : '';
}

/** Converts saved preference data into the controlled input value, falling back to the product default for invalid stored data. */
function normalizeLoadedDefaultAlgoDuration(rawValue: unknown): string {
  const parsedDuration = parseValidDefaultAlgoDuration(stringifyDefaultAlgoDuration(rawValue));
  return String(parsedDuration ?? defaultAlgoDurationMinutes);
}

/** Resolves user-entered input to the value that may be persisted, using 15 for blank or zero per Preferences behavior. */
function resolveDefaultAlgoDurationForSave(value: string): number | undefined {
  const trimmedValue = value.trim();
  if (trimmedValue === '' || trimmedValue === '0') {
    return defaultAlgoDurationMinutes;
  }

  return parseValidDefaultAlgoDuration(trimmedValue);
}

/** Renders QEP preferences and keeps the default algo duration within the range Order Entry supports. */
const qepPrefs = () => {
  const {data, isFetching} = usePreferences();
  const dispatch = useDispatch();
  const selectedDefaultAlgoDuration = useSelector((state: RootReducer) => state.qepPreferencesReducer.defaultAlgoDuration);

  const [hasError, setHasError] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<string>('');
  const [revertValue, setRevertValue] = useState<string>(String(defaultAlgoDurationMinutes));

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(null);

  useEffect(() => {
    if (isFetching) {
      return;
    }

    const finalValue = normalizeLoadedDefaultAlgoDuration(data?.['qep.defaultAlgoDuration']);
    const numericFinal = Number(finalValue);

    dispatch(setDefaultAlgoDuration({
      duration: numericFinal,
      initialLoad: true,
    }));
    setLocalValue(finalValue);
    setRevertValue(finalValue);
  }, [data, dispatch, isFetching]);

  const handleSave = (valueToSave: string = localValue) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const durationToSave = resolveDefaultAlgoDurationForSave(valueToSave);

    if (durationToSave === undefined) {
      setHasError(true);
      setLocalValue(revertValue);
      getQepSystem().notification.local.error(invalidAlgoDurationMessage);
      return;
    }

    const nextValue = String(durationToSave);

    if (localValue !== nextValue) {
      setLocalValue(nextValue);
    }

    setRevertValue(nextValue);

    if (durationToSave !== selectedDefaultAlgoDuration) {
      dispatch(setDefaultAlgoDuration({duration: durationToSave}));
    }

    setHasError(false);
  };

  const hasData: boolean = selectedDefaultAlgoDuration !== null && selectedDefaultAlgoDuration !== undefined;

  return (
    <div className='qep-preferences'>
      {(isFetching || !hasData)
        ? <LoadingIndicator />
        : (
          <div>
            <label>Default Algo Duration (in minutes)</label>
            <QEPInput
              className={hasError ? 'error' : ''}
              type='number'
              value={localValue}
              placeholder={String(defaultAlgoDurationMinutes)}
              min={minAlgoDurationMinutes}
              max={maxAlgoDurationMinutes}
              step={1}
              onFocus={event => {
                event.target.select();
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  handleSave();
                } else if (event.key === 'Escape') {
                  setLocalValue(revertValue);
                  setHasError(false);
                }
              }}
              onBlur={event => {
                const {value, validity} = event.target;
                if (validity.badInput || !/^\d*$/.test(value)) {
                  setHasError(true);
                  getQepSystem().notification.local.error(invalidAlgoDurationMessage);
                  return;
                }

                handleSave(value);
              }}
              onChange={event => {
                const {value, validity} = event.target;

                if (validity.badInput || !/^\d*$/.test(value)) {
                  return;
                }

                const durationToSave = resolveDefaultAlgoDurationForSave(value);
                if (durationToSave === undefined) {
                  return;
                }

                if (value === '0') {
                  handleSave(String(defaultAlgoDurationMinutes));
                  return;
                }

                const nextValue = value === '' ? value : String(durationToSave);

                setLocalValue(nextValue);
                setHasError(false);

                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                }

                if (nextValue !== '') {
                  timeoutRef.current = setTimeout(() => {
                    handleSave(nextValue);
                  }, 700);
                }
              }}
            />
          </div>
        )
      }
    </div>
  );
};

export default qepPrefs;
